import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { MoneyInput } from "@/components/MoneyInput";
import { toNum } from "@/lib/format";
import { toast } from "sonner";
import { Save, Upload, Image as ImageIcon, User, Trash2, MessageCircle, Loader2, RefreshCw, CheckCircle2, XCircle, Plus } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [openUser, setOpenUser] = useState(false);
  const [qrisPreview, setQrisPreview] = useState("");
  const [waStatus, setWaStatus] = useState(null);
  const [checkingWa, setCheckingWa] = useState(false);

  const load = () => {
    Promise.allSettled([
      api.get("/settings"),
      user?.role === "Admin" ? api.get("/auth/users") : Promise.resolve({ data: [] }),
    ]).then(([s, u]) => {
      if (s.status === "fulfilled") {
        setSettings(s.value.data);
        setQrisPreview(s.value.data.qris_image || "");
      }
      if (u.status === "fulfilled") setUsers(u.value.data || []);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    const payload = {
      business_name: settings.business_name,
      business_address: settings.business_address,
      boss_wa_number: settings.boss_wa_number,
      fonnte_token: settings.fonnte_token_new || undefined,
      qris_image: qrisPreview,
      modal_awal: toNum(settings.modal_awal),
      admin_tiers_normal: (settings.admin_tiers_normal || []).map((t) => ({ up_to: toNum(t.up_to), fee: toNum(t.fee) })),
      admin_tiers_potongan: (settings.admin_tiers_potongan || []).map((t) => ({ up_to: toNum(t.up_to), fee: toNum(t.fee) })),
    };
    await api.post("/settings", payload);
    toast.success("Pengaturan disimpan"); load();
  };

  const checkWa = async () => {
    setCheckingWa(true);
    try {
      const r = await api.get("/settings/wa-status");
      setWaStatus(r.data);
      if (r.data.connected) toast.success("WhatsApp Anda tersambung ✓");
      else if (r.data.configured) toast.error("Token ada, tapi WhatsApp belum tersambung. Scan QR lagi di Fonnte.");
      else toast.error("Token belum disimpan. Tempel token lalu Simpan dulu.");
    } finally { setCheckingWa(false); }
  };

  const onQrisUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar");
    if (file.size > 8 * 1024 * 1024) return toast.error("Ukuran maks 8MB");
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1000;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const r = Math.min(MAX / width, MAX / height);
          width = Math.round(width * r);
          height = Math.round(height * r);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        setQrisPreview(canvas.toDataURL("image/jpeg", 0.85));
        toast.success("Gambar QRIS siap. Klik Simpan Pengaturan.");
      };
      img.onerror = () => toast.error("Gambar tidak bisa dibaca");
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  };

  if (!settings) return <Skeleton className="h-96 w-full" />;
  const isAdmin = user?.role === "Admin";

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Konfigurasi</p>
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Pengaturan</h1>
      </div>

      <Card className="p-5 space-y-4">
        <p className="font-semibold">Profil Usaha & Modal</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Nama Usaha</Label><Input data-testid="set-biz-name" value={settings.business_name || ""} onChange={(e) => setSettings({ ...settings, business_name: e.target.value })} /></div>
          <div><Label>No WA Bos</Label><Input data-testid="set-boss-wa" value={settings.boss_wa_number || ""} onChange={(e) => setSettings({ ...settings, boss_wa_number: e.target.value })} placeholder="628xxx" /></div>
          <div className="md:col-span-2"><Label>Alamat</Label><Textarea data-testid="set-biz-address" value={settings.business_address || ""} onChange={(e) => setSettings({ ...settings, business_address: e.target.value })} rows={2} /></div>
          <div className="md:col-span-2">
            <Label>Modal Awal (Rp) <span className="text-xs text-muted-foreground font-normal">— dipakai di Rekapitulasi (kode A)</span></Label>
            <MoneyInput data-testid="set-modal-awal" value={settings.modal_awal ?? ""} onChange={(v) => setSettings({ ...settings, modal_awal: v })} placeholder="50.000.000" />
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-4" data-testid="admin-tiers-card">
        <div>
          <p className="font-semibold">Tarif Biaya Admin Otomatis</p>
          <p className="text-xs text-muted-foreground mt-0.5">Biaya admin terisi otomatis sesuai nominal transaksi (mode Admin: Tarik/Kirim/Pembayaran) — tetap bisa diubah manual. Tarif berlaku bila nominal &lt; batas.</p>
        </div>
        <TierTable
          title="Tanpa potongan (sesama / normal)"
          testid="tier-normal"
          tiers={settings.admin_tiers_normal || []}
          onChange={(v) => setSettings({ ...settings, admin_tiers_normal: v })}
        />
        <TierTable
          title="Dengan potongan (antar bank)"
          testid="tier-potongan"
          tiers={settings.admin_tiers_potongan || []}
          onChange={(v) => setSettings({ ...settings, admin_tiers_potongan: v })}
        />
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-emerald-500/10 grid place-items-center shrink-0">
            <MessageCircle className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold">Hubungkan WhatsApp (Fonnte)</p>
            <p className="text-xs text-muted-foreground">Agar laporan & tagihan terkirim otomatis dari WhatsApp Anda sendiri.</p>
          </div>
        </div>

        <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal pl-4 marker:text-primary marker:font-semibold">
          <li>Daftar / masuk di <a href="https://fonnte.com" target="_blank" rel="noreferrer" className="underline text-primary">fonnte.com</a>.</li>
          <li>Buka menu <b>Device → Add Device</b>, isi nama & nomor WhatsApp Anda.</li>
          <li>Klik <b>Connect</b>, lalu di HP: <b>WhatsApp → Perangkat Tertaut → Tautkan Perangkat</b>, scan QR-nya.</li>
          <li>Klik <b>Token</b> pada device tsb, salin, tempel di bawah ini, lalu <b>Simpan Pengaturan</b>.</li>
          <li>Terakhir, klik <b>Cek Koneksi</b> untuk memastikan WhatsApp Anda sudah tersambung.</li>
        </ol>

        <div>
          <Label>Fonnte Token {settings.fonnte_token_set && <Badge variant="secondary" className="ml-2">Terkonfigurasi</Badge>}</Label>
          <Input data-testid="set-fonnte-token" type="password" value={settings.fonnte_token_new || ""} onChange={(e) => setSettings({ ...settings, fonnte_token_new: e.target.value })} placeholder="Tempel token device di sini" />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button data-testid="btn-check-wa" type="button" variant="outline" size="sm" onClick={checkWa} disabled={checkingWa} className="gap-2">
            {checkingWa ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Cek Koneksi
          </Button>
          {waStatus && (
            waStatus.connected ? (
              <span className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5" data-testid="wa-status">
                <CheckCircle2 className="h-4 w-4" /> Tersambung{waStatus.device ? ` · ${waStatus.device}` : ""}{waStatus.quota != null ? ` · sisa kuota ${waStatus.quota}` : ""}
              </span>
            ) : waStatus.configured ? (
              <span className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1.5" data-testid="wa-status">
                <XCircle className="h-4 w-4" /> Belum tersambung — scan QR lagi di Fonnte.
              </span>
            ) : (
              <span className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="wa-status">
                <XCircle className="h-4 w-4" /> Belum dikonfigurasi. Simpan token dulu.
              </span>
            )
          )}
        </div>
        <p className="text-xs text-muted-foreground">Jika token kosong, tombol Kirim WA tetap bisa dipakai secara manual (membuka aplikasi WhatsApp dengan pesan sudah terisi).</p>
      </Card>

      <Card className="p-5 space-y-4">
        <p className="font-semibold">QRIS Statis</p>
        <p className="text-xs text-muted-foreground">Upload gambar QRIS statis Anda. Akan dilampirkan pada pesan tagihan.</p>
        <div className="flex items-start gap-4 flex-wrap">
          {qrisPreview ? (
            <div className="relative">
              <img
                src={qrisPreview}
                alt="QRIS"
                className="h-40 w-40 object-contain rounded-lg border bg-white"
                data-testid="qris-preview"
                onLoad={(e) => {
                  if (e.target.naturalWidth <= 2 || e.target.naturalHeight <= 2) {
                    setQrisPreview("");
                    toast.error("Gambar QRIS lama tidak valid. Silakan upload ulang.");
                  }
                }}
                onError={() => { setQrisPreview(""); toast.error("Gambar QRIS lama rusak. Silakan upload ulang."); }}
              />
              <Button variant="destructive" size="icon" className="absolute -top-2 -right-2 h-7 w-7 rounded-full" onClick={() => setQrisPreview("")}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <label data-testid="qris-upload-label" className="h-40 w-40 border-2 border-dashed border-border rounded-lg grid place-items-center cursor-pointer hover:bg-accent">
              <div className="text-center">
                <ImageIcon className="h-6 w-6 mx-auto text-muted-foreground" />
                <p className="text-xs text-muted-foreground mt-2">Klik untuk upload</p>
              </div>
              <input data-testid="qris-upload" type="file" accept="image/*" className="hidden" onChange={onQrisUpload} />
            </label>
          )}
        </div>
      </Card>

      <div className="sticky bottom-16 lg:bottom-0 bg-background py-3 border-t z-10">
        <Button data-testid="btn-save-settings" size="lg" className="w-full lg:w-auto gap-2" onClick={save}>
          <Save className="h-4 w-4" /> Simpan Pengaturan
        </Button>
      </div>

      {isAdmin && (
        <Card className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="font-semibold">Manajemen Pengguna</p>
            <Button data-testid="btn-new-user" size="sm" onClick={() => setOpenUser(true)}>
              <User className="h-3.5 w-3.5 mr-1" /> Tambah User
            </Button>
          </div>
          <div className="divide-y">
            {users.map((u) => (
              <div key={u.id} className="py-2 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-primary/20 text-primary grid place-items-center font-semibold text-sm">{u.name[0]}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">{u.name}</p>
                  <p className="text-xs text-muted-foreground">@{u.username}</p>
                </div>
                <Badge variant="secondary">{u.role}</Badge>
                {u.disabled && <Badge variant="destructive">Nonaktif</Badge>}
              </div>
            ))}
          </div>
        </Card>
      )}

      <UserDialog open={openUser} onClose={() => setOpenUser(false)} onSaved={load} />
    </div>
  );
}

function TierTable({ title, tiers, onChange, testid }) {
  const setRow = (i, k, v) => onChange(tiers.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const addRow = () => onChange([...tiers, { up_to: "", fee: "" }]);
  const removeRow = (i) => onChange(tiers.filter((_, idx) => idx !== i));
  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid={testid}>
      <p className="text-sm font-medium">{title}</p>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center text-[11px] text-muted-foreground">
        <span>Nominal di bawah (Rp)</span><span>Biaya admin (Rp)</span><span className="w-9" />
      </div>
      {tiers.map((t, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`${testid}-row-${i}`}>
          <MoneyInput data-testid={`${testid}-up-${i}`} value={t.up_to} onChange={(v) => setRow(i, "up_to", v)} />
          <MoneyInput data-testid={`${testid}-fee-${i}`} value={t.fee} onChange={(v) => setRow(i, "fee", v)} />
          <Button type="button" size="icon" variant="ghost" className="h-9 w-9 text-muted-foreground hover:text-destructive" onClick={() => removeRow(i)} data-testid={`${testid}-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="gap-1 text-xs" onClick={addRow} data-testid={`${testid}-add`}><Plus className="h-3 w-3" /> Tambah tingkat</Button>
    </div>
  );
}

function UserDialog({ open, onClose, onSaved }) {
  const empty = { username: "", password: "", name: "", role: "Kasir" };
  const [form, setForm] = useState(empty);
  useEffect(() => { setForm(empty); /* eslint-disable-next-line */ }, [open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.username || !form.password || !form.name) return toast.error("Lengkapi field");
    await api.post("/auth/register", form);
    toast.success("User dibuat"); onSaved?.(); onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>User Baru</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama</Label><Input data-testid="user-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>Username</Label><Input data-testid="user-username" value={form.username} onChange={(e) => set("username", e.target.value)} /></div>
          <div><Label>Password</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Admin">Admin</SelectItem>
                <SelectItem value="Kasir">Kasir</SelectItem>
                <SelectItem value="Bos">Bos</SelectItem>
                <SelectItem value="Viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button data-testid="btn-save-user" onClick={save} className="flex-1">Simpan</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
